import { useEffect, useRef } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { useDispatch, useSelector } from "react-redux";

import {
    setExpandedFilterName,
    setFilterMode,
    setIsFiltersCategoriesPanelExpanded,
    setSelectedFilterCategory,
} from "app/pages/sales-intelligence/modules/advanced-search-filtering/store/action-creators";
import { leadsFilterCategoryMap } from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsFilterCategory";
import { selectIsInitialFiltersReady } from "app/pages/sales-intelligence/modules/advanced-search-filtering/store/selectors";
import { INDUSTRY_TAXONOMY_DEMO_ID } from "app/pages/sales-intelligence/modules/industry/constants";
import { useIsIndustryTaxonomyOptedIn } from "app/pages/sales-intelligence/modules/industry/hooks/useIsIndustryTaxonomyOptedIn";
import { useIndustryTaxonomyDemoSession } from "app/pages/sales-intelligence/modules/leads-search-filters/hooks/useIndustryTaxonomyDemoSession";
import { useDemoModeStore } from "app/pages/sales-intelligence/store/demo-mode/useDemoModeStore";

import type { PageRoutingParams } from "../types";

/**
 * Resolves a `demo` transition param into one of two arrivals — the composition root that turns the id
 * another page published into something that means anything on *this* page.
 *
 * The param is a one-shot command, not state: it is transition-only (never in the URL) and declared
 * `inherit: false`, so it is delivered exactly once and there is nothing to clean up afterwards.
 *
 * For an account that has not opted in, it resolves into a demo session whose `onEnter` dispatches into
 * this page's filter panel. For one that has, there is no demo to preview, and the param instead reads as
 * the launch banner's "show me the taxonomy" — the same two panel dispatches the session would make, minus
 * the session. That reuse is temporary; a second param would be the honest shape if this outlives the rollout.
 *
 * Either arrival waits for the initial filters to settle (ADR-0014). `onEnter` writes the filters slice, and
 * so do `useApplyInitialFilters` / `useApplyTerritoryFilters` / the saved-search fetch; acting first would let
 * a late seed overwrite the demo's state or collapse the card just expanded.
 */
export const useDemoFromRouteParam = (): void => {
    const { params } = useCurrentStateAndParams<PageRoutingParams>();

    const dispatch = useDispatch();
    const isInitialFiltersReady = useSelector(selectIsInitialFiltersReady);

    const isIndustryTaxonomyOptedIn = useIsIndustryTaxonomyOptedIn();
    const session = useIndustryTaxonomyDemoSession();

    const hasEnteredRef = useRef(false);

    useEffect(() => {
        // Compared as a plain string: the param is untyped at the routing boundary, so the branded `DemoId`
        // is only earned by matching a known id.
        if (hasEnteredRef.current || params.demo !== INDUSTRY_TAXONOMY_DEMO_ID) {
            return;
        }

        if (!isInitialFiltersReady) {
            return;
        }

        // Already opted in: the taxonomy is permanent for this account, so a preview of it means nothing —
        // open the card the launch banner promised instead. Both dispatches mirror the session's `onEnter`:
        // without `setFilterMode`, the expansion lands in a hidden panel if the user was on the agent tab.
        if (isIndustryTaxonomyOptedIn) {
            hasEnteredRef.current = true;

            dispatch(setSelectedFilterCategory(leadsFilterCategoryMap.company));
            dispatch(setIsFiltersCategoriesPanelExpanded(true));
            dispatch(setFilterMode("filters-list"));
            dispatch(setExpandedFilterName("industryTaxonomy"));

            return;
        }

        const { activeDemo, enteringDemo } = useDemoModeStore.getState();

        if (activeDemo ?? enteringDemo) {
            return;
        }

        hasEnteredRef.current = true;
        useDemoModeStore.getState().enterDemo(session);
    }, [params.demo, isIndustryTaxonomyOptedIn, isInitialFiltersReady, session, dispatch]);
};
